/**
 * Support Triage Workflow
 *
 * This workflow automatically triages incoming support requests:
 * 1. Triggers when a new support request is created
 * 2. Analyzes content to classify priority and category
 * 3. Checks if issue can be auto-resolved
 * 4. Either auto-assigns or notifies support team
 */

import type { Workflow } from './types';

// =============================================================================
// WORKFLOW DEFINITION
// =============================================================================

export const supportTriageWorkflow: Workflow = {
  id: 'support-triage',
  name: 'Triage Richieste Supporto',
  description: 'Classifica automaticamente le richieste di supporto per priorità e categoria',
  trigger: {
    type: 'event',
    config: {
      eventType: 'entity.created',
      entityType: 'support_request',
    },
  },
  steps: [
    {
      id: 'analyze_content',
      name: 'Analizza Contenuto',
      type: 'ai_analyze',
      config: {
        prompt: `Analyze this support request and classify it.

Request:
Subject: {subject}
Description: {description}
User: {userName}

Classify:
1. Priority (critical/high/medium/low):
   - CRITICAL: System down, data loss, security issue
   - HIGH: Major feature broken, blocking work
   - MEDIUM: Feature issue, workaround available
   - LOW: Enhancement request, minor issue

2. Category:
   - bug: Software defect
   - feature_request: New feature or enhancement
   - how_to: Usage question
   - access: Login/permission issue
   - performance: Speed/reliability issue
   - other: Unclassified

3. Auto-resolvable: Can this be resolved automatically?

Respond in JSON:
{
  "priority": "critical|high|medium|low",
  "category": "bug|feature_request|how_to|access|performance|other",
  "autoResolvable": true/false,
  "suggestedResolution": "If auto-resolvable, how to resolve",
  "keywords": ["keyword1", "keyword2"],
  "estimatedComplexity": "simple|moderate|complex"
}`,
      },
      next: ['check_auto_resolve'],
    },
    {
      id: 'check_auto_resolve',
      name: 'Verifica Auto-Risoluzione',
      type: 'custom',
      config: {
        // Check if issue matches known auto-resolvable patterns
        checkKnowledgeBase: true,
        autoResolvableCategories: ['how_to', 'access'],
        requireConfidence: 0.90,
      },
      condition: {
        field: 'autoResolvable',
        operator: '==',
        value: true,
      },
      next: ['assign_or_notify'],
    },
    {
      id: 'assign_or_notify',
      name: 'Assegna o Notifica',
      type: 'custom',
      config: {
        // Auto-assign based on category and team availability
        autoAssignRules: {
          bug: 'engineering_team',
          feature_request: 'product_team',
          how_to: 'support_team',
          access: 'admin_team',
        },
        escalateIfCritical: true,
        notifyTeamChannel: true,
      },
      next: ['send_notification'],
    },
    {
      id: 'send_notification',
      name: 'Notifica Team',
      type: 'send_notification',
      config: {
        type: 'in_app',
        message: 'Nuova richiesta supporto: {subject} | Priorità: {priority} | Categoria: {category}',
        notifyAssignee: true,
        includeTriage: true,
      },
    },
  ],
  enabled: true,
  version: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Calculate priority score from classification
 */
export function calculatePriorityScore(priority: string): number {
  const scores = {
    critical: 100,
    high: 75,
    medium: 50,
    low: 25,
  };
  return scores[priority as keyof typeof scores] || 0;
}
